import { ComponentProps, forwardRef, useState } from "react";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils";
import { Button, ButtonProps } from "../button/button";
import { Input } from "../input";
import { Label } from "../label";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { TimeColumn } from "./TimeColumn";
import { useTimeInputLogic } from "./useTimeInputLogic";

export interface TimeInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  showSeconds?: boolean;
  use24Hour?: boolean;
  min?: string;
  max?: string;
  stepMinutes?: number;
  stepSeconds?: number;
  inputProps?: Omit<
    ComponentProps<typeof Input>,
    "value" | "onChange" | "onFocus" | "onBlur" | "onKeyDown" | "id" | "ref" | "disabled" | "placeholder"
  >;
  buttonProps?: Omit<ButtonProps, "disabled">;
}

export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(
  (
    {
      value,
      onChange,
      onBlur,
      label,
      placeholder,
      disabled = false,
      className = "",
      id = "time-input",
      showSeconds = false,
      use24Hour = false,
      min,
      max,
      stepMinutes = 15,
      stepSeconds = 15,
      inputProps: { className: inputClassName, ...inputProps } = {},
      buttonProps: { className: buttonClassName, ...buttonProps } = {},
    },
    ref
  ) => {
    const { t } = useTranslation("ui");
    const [open, setOpen] = useState(false);
    const [scrollKey, setScrollKey] = useState(0);

    const {
      displayValue,
      hourItems,
      minuteItems,
      secondItems,
      periodItems,
      selectedDisplayHour,
      selectedMinute,
      selectedSecond,
      selectedPeriod,
      setDisplayHour,
      setMinute,
      setSecond,
      setPeriod,
      handleInputChange,
      handleInputFocus,
      handleInputBlur,
    } = useTimeInputLogic({
      value,
      onChange,
      showSeconds,
      use24Hour,
      min,
      max,
      stepMinutes,
      stepSeconds,
    });

    const handleOpenChange = (next: boolean) => {
      setOpen(next);
      if (next) setScrollKey((k) => k + 1);
    };

    const resolvedPlaceholder = (() => {
      if (placeholder) return placeholder;
      if (use24Hour) return showSeconds ? "09:30:00" : "09:30";
      return showSeconds ? "9:30:00 AM" : "9:30 AM";
    })();

    const handleBlur = () => {
      handleInputBlur();
      onBlur?.();
    };

    return (
      <div
        data-slot="time-input"
        className={cn("flex flex-col", className?.includes("space-y") ? "" : "gap-1.5", className)}
      >
        {label && (
          <Label htmlFor={id} className="px-1">
            {label}
          </Label>
        )}
        <div className="relative">
          <Input
            ref={ref}
            id={id}
            value={displayValue}
            placeholder={resolvedPlaceholder}
            className={cn("bg-background pr-10", inputClassName)}
            disabled={disabled}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                handleOpenChange(true);
              }
            }}
            {...inputProps}
          />
          <Popover open={open} onOpenChange={handleOpenChange} modal>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className={cn("absolute top-1/2 right-2 size-6 -translate-y-1/2", buttonClassName)}
                disabled={disabled}
                {...buttonProps}
              >
                <Clock className="size-3.5" />
                <span className="sr-only">{t("Select time")}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="end" alignOffset={-8} sideOffset={10}>
              <div className="flex items-stretch gap-1">
                <TimeColumn
                  ariaLabel={t("Hour")}
                  items={hourItems}
                  selected={selectedDisplayHour}
                  onSelect={setDisplayHour}
                  scrollKey={scrollKey}
                  className="w-12"
                />
                <div className="flex items-center text-sm text-muted-foreground">:</div>
                <TimeColumn
                  ariaLabel={t("Minute")}
                  items={minuteItems}
                  selected={selectedMinute}
                  onSelect={setMinute}
                  scrollKey={scrollKey}
                  className="w-12"
                />
                {showSeconds && secondItems && (
                  <>
                    <div className="flex items-center text-sm text-muted-foreground">:</div>
                    <TimeColumn
                      ariaLabel={t("Second")}
                      items={secondItems}
                      selected={selectedSecond}
                      onSelect={setSecond}
                      scrollKey={scrollKey}
                      className="w-12"
                    />
                  </>
                )}
                {!use24Hour && periodItems && (
                  <TimeColumn
                    ariaLabel={t("Period")}
                    items={periodItems}
                    selected={selectedPeriod}
                    onSelect={setPeriod}
                    scrollKey={scrollKey}
                    className="w-14 ml-1"
                  />
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    );
  }
);
TimeInput.displayName = "TimeInput";
